/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {dlsColor} from '../../theme/theme';
import {Indicators} from '../indicators/Indicator';

/**
 * label (required): This prop is used to specify the primary text label.
 * tag (optional): This prop contains label and backgroundColor optional prop used to display
 * the tags sent, alongside the primary text label.
 */
export interface PrimaryTextProps {
  label: string;
  tag?: {
    label: string;
    backgroundColor?: string;
  };
  isNew: boolean;
};

// defines the styles definition for PrimaryText component...
export const useStyles = makeStyles(() => ({
  beta : {
    display      : 'inline',
    marginLeft   : '10px',
    color        : dlsColor('white'),
    padding      : '2px 4px 2px 4px',
    borderRadius : '3px',
    fontFamily   : 'Roboto'
  },
  newBadge : {
    display         : 'inline-flex',
    alignItems      : 'center',
    justifyContent  : 'center',
    height          : '16px',
    padding         : '0 5px',
    fontSize        : '9px',
    fontWeight      : 700,
    lineHeight      : 1,
    letterSpacing   : '0.3px',
    color           : '#FFFFFF',
    backgroundColor : '#c218f0',
    borderRadius    : '2px',
    whiteSpace      : 'nowrap'
  }
}));

/**
 * The PrimaryText component is a reusable React component designed
 * for rendering primary text labels with an optional "Beta" tag.
 * It's commonly used in user interfaces to display labels with
 * additional information about their status.
 *
 * ```typescript
 *
 *  import {PrimaryText} from '@ion/dls/src/components/navigation/PrimaryText'; // Import the component
 *
 *  function MyComponent() {
 *    return (
 *      <div>
 *        <PrimaryText label="My Label" />
 *        <PrimaryText label="My Beta Label" isBeta />
 *      </div>
 *    );
 *  }
 * ```
 */
export const PrimaryText = memo(({
  label,
  tag,
  isNew
}: PrimaryTextProps) => {
  const {
    label: tagLabel,
    backgroundColor: tagBackgroundColor
  } = tag || {};
  const classes = useStyles();

  return (
    <>
      {label}
      {isNew && (
        <div className={classes.newBadge}>
          {Indicators.NEW}
        </div>
      )}
      {!!tagLabel && (
        <div className={classes.beta}
          style={{backgroundColor: dlsColor(tagBackgroundColor || 'electricPurple')}}>
          {translate(tagLabel)}
        </div>
      )}
    </>
  );
});

export default PrimaryText;
